// The UI's pixel icons (instead of emoji): 16x16 frames of /icons/ui-icons.png, drawn by assets/icons/pixel_icons.py
// (names in /icons/ui-icons.js). Shown at a whole-number scale with image-rendering: pixelated, so they stay crisp:
//   icon('mic')               -> <span class="px-icon" aria-hidden="true"> at 16 CSS px (1x)
//   icon('mic', { size: 32 }) -> 2x
//   label(button, 'mic', '麦克风开 (M)') sets a button to icon + text (what textContent = '🎤 …' used to do)
// Many modules import it by one URL and share one instance (the build bundles it once).
import { SIZE, COLS, ROWS, FRAMES } from '/icons/ui-icons.js';

const SHEET = '/icons/ui-icons.png';
const CSS = `.px-icon{display:inline-block;flex:none;width:var(--px-s,16px);height:var(--px-s,16px);vertical-align:-3px;
background:url(${SHEET}) no-repeat;background-size:calc(var(--px-s,16px) * ${COLS}) calc(var(--px-s,16px) * ${ROWS});
background-position:calc(var(--px-s,16px) * var(--px-x,0) * -1) calc(var(--px-s,16px) * var(--px-y,0) * -1);
image-rendering:pixelated;image-rendering:crisp-edges}
.px-icon.px-2x{--px-s:${SIZE * 2}px;vertical-align:-9px}
.px-lab{display:inline-flex;align-items:center;gap:6px}`;
let styled = false;
function style() {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const s = document.createElement('style'); s.id = 'px-icon-css'; s.textContent = CSS; document.head.appendChild(s);
}

/** el already shows exactly this icon + text (callers re-render on a timer: don't rebuild what's there) */
const same = (el, name, text) => el.dataset.pxk === name + '\u0000' + text && el.firstChild?.dataset?.icon === name;

/** Is there an icon called name? */
export const hasIcon = (name) => Object.prototype.hasOwnProperty.call(FRAMES, name);

/** A <span> showing icon `name`. size: 16 (1x, default) or 32 (2x); other sizes round to a whole multiple of 16. */
export function icon(name, { size = SIZE, title } = {}) {
  style();
  const [x, y] = FRAMES[name] ?? FRAMES.exclaim;
  const k = Math.max(1, Math.round(size / SIZE));
  const el = document.createElement('span');
  el.className = 'px-icon' + (k === 2 ? ' px-2x' : '');
  el.dataset.icon = name;
  el.style.setProperty('--px-x', x); el.style.setProperty('--px-y', y);
  if (k > 2) el.style.setProperty('--px-s', SIZE * k + 'px');
  if (title) { el.title = title; el.setAttribute('role', 'img'); el.setAttribute('aria-label', title); }
  else el.setAttribute('aria-hidden', 'true');
  return el;
}

/** Set el's content to icon + text (e.g. a bar button's label). Returns el. */
export function label(el, name, text, opts) {
  if (same(el, name, text)) return el;
  el.replaceChildren(icon(name, opts), document.createTextNode(text)); el.dataset.pxk = name + '\u0000' + text;
  el.classList.add('px-lab');
  return el;
}

/** el's content becomes icon + ' ' + text, inline (for a status line, a title, a log line); no icon if name is falsy.
 *  Returns el. */
export function put(el, name, text = '') {
  if (!name || !hasIcon(name)) { if (el.textContent !== text || el.firstElementChild) el.textContent = text; delete el.dataset.pxk; return el; }
  if (same(el, name, text)) return el;
  el.replaceChildren(icon(name), document.createTextNode(text ? ' ' + text : '')); el.dataset.pxk = name + '\u0000' + text;
  return el;
}
/** A new element (tag, class) holding icon + text: put() on a fresh element. */
export function iconEl(tag, cls, name, text) { const e = document.createElement(tag); if (cls) e.className = cls; return put(e, name, text); }
/** For HTML templates: every <i data-px="name"></i> under root becomes icon(name). Returns root. */
export function hydrate(root) {
  for (const i of root.querySelectorAll('i[data-px]')) i.replaceWith(icon(i.dataset.px));
  return root;
}
// Emoji an older server (or a module not yet moved over) still puts in front of a line -> the icon for it
const LEAD = { '🌙': 'moon', '☀️': 'sun', '🐺': 'wolf', '🎉': 'party', '🗳️': 'vote', '🔮': 'crystal', '💊': 'potion', '☠️': 'skull', '🕵️': 'spy',
  '💡': 'bulb', '⚔️': 'sword', '🎨': 'palette', '🏆': 'trophy', '👋': 'wave', '🤖': 'robot', '🐟': 'fish', '✨': 'sparkle', '🎤': 'mic' };
/** [icon name, the text after it] if text starts with one of those emoji (and a space), else [fallback, text] */
export function lead(text, fallback = null) {
  const s = String(text ?? '');
  for (const [e, n] of Object.entries(LEAD)) if (s.startsWith(e)) return [n, s.slice(e.length).replace(/^️?\s*/, '')];
  return [fallback, s];
}
/** Draw icon `name` on a 2D canvas at (x, y), size px (a multiple of 16 stays crisp); waits for the atlas the first time. */
let sheetImg = null;
export function drawIcon(g, name, x, y, size = SIZE) {
  const f = FRAMES[name]; if (!f) return;
  if (!sheetImg) { sheetImg = new Image(); sheetImg.src = SHEET; }
  const draw = () => { g.imageSmoothingEnabled = false; g.drawImage(sheetImg, f[0] * SIZE, f[1] * SIZE, SIZE, SIZE, x, y, size, size); };
  if (sheetImg.complete && sheetImg.naturalWidth) draw(); else sheetImg.addEventListener('load', draw, { once: true });
}
